import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/utils/api'

export interface Assignment {
  id: string
  chore_id: string
  assigned_to: string
  status: string
  points_offered: number
  points_earned?: number
  due_date: string
  created_at?: string
  claimed_at?: string
  started_at?: string
  completed_at?: string
  completion_notes?: string
  completion_photo_url?: string
  verification_notes?: string
  verified_at?: string
  photo_url?: string
  assigned_user?: {
    id: string
    username: string
    display_name: string
    color_theme?: string
  }
  chore?: {
    id: string
    name: string
    description?: string
    instructions?: string
    category?: string
    base_points?: number
    estimated_minutes?: number
    difficulty?: string
    requires_verification?: boolean
    requires_photo?: boolean
    icon?: string
    tags?: string[]
  }
  // Legacy fields for compatibility
  user_id?: number
  username?: string
  display_name?: string
  chore_name?: string
  points?: number
  assigned_date?: string
}

export interface AssignmentFilters {
  status?: string
  assignedTo?: string
  dateFilter?: string
  limit?: number
}

// Query keys factory for consistent cache management
export const assignmentKeys = {
  all: ['assignments'] as const,
  lists: () => [...assignmentKeys.all, 'list'] as const,
  list: (filters: AssignmentFilters) => [...assignmentKeys.lists(), filters] as const,
  detail: (id: string) => [...assignmentKeys.all, 'detail', id] as const,
}

/**
 * Fetch assignments with automatic caching and background refetching
 */
export function useAssignments(filters: AssignmentFilters = {}) {
  const { status, assignedTo, dateFilter = 'all', limit = 500 } = filters

  return useQuery({
    queryKey: assignmentKeys.list(filters),
    queryFn: async () => {
      const data = await apiClient.getAssignments(status, assignedTo, dateFilter, limit)
      // Handle various response formats from backend
      if (Array.isArray(data)) {
        return data as Assignment[]
      }
      if (data?.assignments) {
        return data.assignments as Assignment[]
      }
      if (data?.data) {
        return data.data as Assignment[]
      }
      return [] as Assignment[]
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Verify an assignment (approve or reject with points)
 */
export function useVerifyAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      assignmentId: string
      approved: boolean
      verificationNotes?: string
      pointsAwarded?: number
    }) => {
      const { assignmentId, approved, verificationNotes, pointsAwarded } = params
      return await apiClient.verifyAssignment(assignmentId, {
        approved,
        verification_notes: verificationNotes,
        points_awarded: pointsAwarded,
      })
    },
    onSuccess: () => {
      // Invalidate all assignment queries to ensure fresh data everywhere
      queryClient.invalidateQueries({ queryKey: assignmentKeys.all })
    },
  })
}

/**
 * Complete an assignment (child marks as done)
 */
export function useCompleteAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      assignmentId: string
      completionNotes?: string
    }) => {
      const { assignmentId, completionNotes } = params
      return await apiClient.completeAssignment(assignmentId, completionNotes)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.all })
    },
  })
}

/**
 * Parent complete - complete AND verify in one action
 */
export function useParentCompleteAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      assignmentId: string
      notes?: string
      pointsAwarded?: number
    }) => {
      const { assignmentId, notes, pointsAwarded } = params
      return await apiClient.post(`/api/assignments/${assignmentId}/parent-complete`, {
        notes,
        points_awarded: pointsAwarded,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.all })
    },
  })
}

/**
 * Unverify an assignment (remove verification and points)
 */
export function useUnverifyAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (assignmentId: string) => {
      return await apiClient.post(`/api/assignments/${assignmentId}/unverify`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.all })
    },
  })
}

/**
 * Update an assignment (edit due date, status, points, etc.)
 */
export function useUpdateAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      assignmentId: string
      updates: {
        due_date?: string
        assigned_to?: string
        status?: string
        points_offered?: number
        completion_notes?: string
      }
    }) => {
      const { assignmentId, updates } = params
      return await apiClient.put(`/api/assignments/${assignmentId}`, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.all })
    },
  })
}

/**
 * Delete an assignment
 */
export function useDeleteAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (assignmentId: string) => {
      return await apiClient.delete(`/api/assignments/${assignmentId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.all })
    },
  })
}

/**
 * Update assignment status (parent only - uses PUT /api/assignments/:id)
 */
export function useUpdateAssignmentStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      assignmentId: string
      status: string
      notes?: string
    }) => {
      const { assignmentId, status, notes } = params
      return await apiClient.put(`/api/assignments/${assignmentId}`, {
        status,
        notes,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.all })
    },
  })
}

/**
 * Start an assignment (kid marks as in progress)
 */
export function useStartAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (assignmentId: string) => {
      return await apiClient.startAssignment(assignmentId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.all })
    },
  })
}

/**
 * Fetch my assignments (for kid view)
 */
export function useMyAssignments() {
  return useQuery({
    queryKey: [...assignmentKeys.all, 'my'] as const,
    queryFn: async () => {
      const data = await apiClient.getMyAssignments()
      return (data || []) as Assignment[]
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })
}
